import { useState, useEffect } from "react";
import { getToken, onMessage, isSupported } from "firebase/messaging";
import { messaging } from "../config/firebase";
import { toast } from "react-toastify";
import helper from "@utils/helper";
import { useNavigate } from "react-router-dom";

const useNotification = () => {
  const navigate = useNavigate();
  const user = helper.getUser();
  const [fcmToken, setFcmToken] = useState<string | null>(null);

  const requestPermission = async () => {
    try {
      const supported = await isSupported();
      if (!supported) {
        console.warn("Notifications not supported in this browser.");
        return null;
      }

      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        if (!messaging) {
            console.error("Messaging instance not initialized.");
            return null;
        }
        const token = await getToken(messaging);
        if (token) {
          setFcmToken(token);
          return token;
        } else {
          console.log("No registration token available.");
          return null;
        }
      } else {
        console.log("Unable to get permission to notify.");
        return null;
      }
    } catch (error) {
      console.error("An error occurred while retrieving token. ", error);
      return null;
    }
  };

  useEffect(() => {
    let unsubscribe: any = null;

    const setupMessaging = async () => {
      const supported = await isSupported();
      if (supported && messaging) {
        unsubscribe = onMessage(messaging, (payload) => {
          console.log("Foreground Message received: ", payload);
          const { title, body } = payload.notification || {};
          const { screen } = payload.data || {};

          try {
            const text = `${title}`;
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.rate = 0.9;
            const voices = window.speechSynthesis.getVoices();
            const preferredVoice = voices.find(
              (v) =>
                v.name.includes("Google US English") ||
                v.name.includes("Samantha") ||
                v.name.includes("Zira") ||
                (v.lang.includes("en") && v.name.includes("Female")),
            );

            if (preferredVoice) utterance.voice = preferredVoice;
            window.speechSynthesis.speak(utterance);
          } catch (e) {
            console.error("TTS error", e);
          }

          toast.info(
            <div>
              <strong>{title}</strong>
              <div>{body}</div>
            </div>,
            {
              onClick: () => {
                if (user?.role === "admin") {
                  if (screen === "Withdrawals") {
                    navigate("/admin/transaction/withdraw-request");
                  }
                } else {
                  if (screen === "Withdrawals") {
                    navigate("/store/view?tab=Withdraw");
                  }
                }
              },
            },
          );
        });
      }
    };

    requestPermission();
    setupMessaging();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return { fcmToken, requestPermission };
};

export default useNotification;
